import { useFormState } from '@vtex/admin-ui-form'
import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import type { BIN } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../Drawer'
import { Alert } from './Alert'
import { BINField } from './BINField'
import { BinFileInput } from './BinFileInput'
import { messages } from './messages'

interface Props {
  state: UseDrawerStateReturn
  config: BIN | undefined
  onSave: (config: BIN) => void
}

export const BIN_SUB_FORM_NAME = 'bin'

type BinSubFormType = { bin: BIN }

export function BINEditor(props: Props) {
  const { state, config, onSave } = props
  const { hide } = state

  const isCreating = !config || config.length === 0

  const { formatMessage: i18n } = useIntl()

  const defaultValues = useMemo<BinSubFormType>(
    () => ({ [BIN_SUB_FORM_NAME]: config ? [...config] : [] }),
    [config]
  )

  const subFormBin = useFormState<BinSubFormType>({
    defaultValues,
  })

  useEffect(() => {
    state.open && subFormBin.reset({ [BIN_SUB_FORM_NAME]: config ?? [] })
  }, [config, state.open, subFormBin])

  const handleSave = () => {
    onSave(subFormBin.getValues(BIN_SUB_FORM_NAME))
    hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{i18n(messages.title)}</DrawerTitle>
        <DrawerSubtitle>{i18n(messages.subtitle)}</DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <BINField state={subFormBin} />
        <BinFileInput state={subFormBin} />
        <Alert state={subFormBin} />
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton onClick={handleSave} isCreating={isCreating} />
      </DrawerFooter>
    </Drawer>
  )
}
